import Link from 'next/link'
import { formatDisplayTitle } from 'src/_helpers/formatDisplayTitle'
import Tag from './Tag'
import ProductTileImage from './ProductTileImage'
import PlusCircle from 'public/icons/interface/plus-circle.svg'
import { resolveLabel } from 'src/components/list/helpers/resolveLabel'
import { useTranslateEntry } from 'src/i18n/client/useTranslateEntry'
import FormattedTitle from 'src/components/FormattedTitle'

// Tag z `labelKey` bierze tekst z tłumaczeń, a `label` zostaje na wypadek braku wpisu w bazie.
const normalizeTags = (tags, translateEntry) =>
  (tags || []).map((tag, index) => {
    if (typeof tag === 'string') {
      return { label: tag, type: 'default', key: `${tag}-${index}` }
    }
    const { labelKey, type = 'default' } = tag
    const label = resolveLabel(translateEntry, labelKey, tag.label)
    return { label, type, key: `${label}-${index}` }
  })

export default function ProductTile({
  imageSrc = null,
  imageAlt = '',
  tags = [],
  maxVisibleTags = 3,
  title,
  subtitle,
  metadata = [],
  footerLines = [],
  ctaLabel,
  ctaHref = '#',
  className = '',
  children,
  isArticle = false,
  ...props
}) {
  const translateEntry = useTranslateEntry()
  // `null` wyłącza przycisk, brak propa daje domyślne „Zobacz więcej” z tłumaczeń.
  const resolvedCtaLabel = ctaLabel === undefined ? translateEntry('T_SEE_MORE') : ctaLabel
  const combinedClassName = `product-tile group ${className}`.trim()
  const normalizedTags = normalizeTags(tags, translateEntry)
  const visibleTags = maxVisibleTags > 0 ? normalizedTags.slice(0, maxVisibleTags) : normalizedTags
  const hiddenTags = normalizedTags.slice(visibleTags.length)
  const Component = ctaHref ? Link : 'div'
  const componentProps = ctaHref ? { href: ctaHref } : {}
  const hasMetadata = metadata?.length > 0
  const hasFooterLines = footerLines?.length > 0
  // Kafel bez linku nie pokazuje CTA — inaczej obiecuje przejście, którego nie ma.
  const hasCta = Boolean(ctaHref && resolvedCtaLabel)
  const hasDetails = hasMetadata || hasFooterLines || hasCta
  const displayTitle = formatDisplayTitle(title || translateEntry('T_UNTITLED'))

  return (
    <Component className={combinedClassName} {...componentProps} {...props}>
      <ProductTileImage src={imageSrc} alt={imageAlt} width={190} height={278} />

      {normalizedTags.length > 0 && (
        <div className="product-tile__tags">
          {visibleTags.map(({ key, label, type }) => (
            <Tag key={key} label={label} type={type} />
          ))}

          {hiddenTags.length > 0 && (
            <span className="product-tile__tags-more" title={hiddenTags.map(({ label }) => label).join(', ')}>
              + {hiddenTags.length}
            </span>
          )}
        </div>
      )}

      <div className="product-tile__content">
        {displayTitle && (
          <p className={`product-tile__title ${isArticle ? 'line-clamp-4' : 'line-clamp-2'}`}>
            {typeof title === 'string' && title ? (
              <FormattedTitle value={title} fallback={displayTitle} />
            ) : (
              displayTitle
            )}
          </p>
        )}

        {subtitle && <p className="product-tile__subtitle">{subtitle}</p>}

        {children}

        {hasDetails && (
          <div className="product-tile__details">
            {(hasMetadata || hasCta) && (
              <div className="product-tile__hover-details">
                {hasMetadata && (
                  <ul className="product-tile__metadata">
                    {metadata.map((line, index) => (
                      <li key={`${line}-${index}`}>{line}</li>
                    ))}
                  </ul>
                )}

                {hasCta && (
                  <span className="product-tile__cta" aria-hidden="true">
                    <span>{resolvedCtaLabel}</span>
                    <PlusCircle />
                  </span>
                )}
              </div>
            )}

            {hasFooterLines && (
              <div className="product-tile__footer">
                {footerLines.map((line, index) => (
                  <p key={`${line}-${index}`}>{line}</p>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </Component>
  )
}
